import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check, X } from 'lucide-react';

/* Lightweight searchable single-select.
 * Props:
 *   options: [{ id, name, slug?, hint?, disabled? }]
 *   value: id or ''
 *   onChange: (id) => void
 *   placeholder, required, disabled, testId
 */
const SearchableSelect = ({ options = [], value, onChange, placeholder = 'Select…', required, disabled, testId, allowClear = true }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const current = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  useEffect(() => {
    const onClickAway = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.name || '').toLowerCase().includes(q) ||
      (o.slug || '').toLowerCase().includes(q) ||
      (o.hint || '').toLowerCase().includes(q),
    );
  }, [options, query]);

  return (
    <div ref={rootRef} className="relative" data-testid={testId}>
      {/* Hidden input so the parent form's HTML5 required works */}
      <input
        type="text"
        value={value || ''}
        required={required}
        readOnly
        tabIndex={-1}
        aria-hidden
        className="sr-only pointer-events-none absolute opacity-0"
      />
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={`w-full h-10 text-sm border border-gray-300 rounded px-2 flex items-center justify-between bg-white focus:outline-none focus:border-[#ee5e23] ${
          disabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:border-gray-400'
        }`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className="truncate text-left">
          {current ? (
            <>
              <span className="text-gray-900">{current.name}</span>
              {current.hint && <span className="text-gray-400 text-xs ml-1">· {current.hint}</span>}
            </>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {allowClear && current && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="text-gray-400 hover:text-red-500 p-0.5"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-[#ee5e23]"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="overflow-y-auto max-h-56" data-testid={testId ? `${testId}-list` : undefined}>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-gray-400">No matches</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { onChange(o.id); setOpen(false); setQuery(''); }}
                  className={`w-full text-left text-sm px-3 py-2 flex items-center justify-between hover:bg-orange-50 transition-colors ${
                    o.id === value ? 'bg-orange-50 text-[#ee5e23]' : 'text-gray-800'
                  }`}
                  data-testid={`${testId || 'opt'}-item-${o.id}`}
                  disabled={o.disabled}
                >
                  <span className="min-w-0 truncate">
                    {o.name}
                    {o.hint && <span className="text-gray-400 text-xs ml-1">· {o.hint}</span>}
                  </span>
                  {o.id === value && <Check className="w-4 h-4 shrink-0" />}
                </button>
              ))
            )}
          </div>
          <div className="px-3 py-1.5 text-[11px] text-gray-400 border-t border-gray-100">
            {filtered.length} of {options.length}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
